import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard, Calendar, Settings, Shield, Trophy, Bell, UserCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isLanding = location.pathname === '/';

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, location.pathname]);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data);
      setNotifCount(data.filter(n => !n.read).length);
    } catch {}
  };

  const markRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, { withCredentials: true });
      setNotifCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLink = (to, label, testId) => (
    <Link to={to} className={`font-sans text-sm font-medium transition-colors duration-200 ${location.pathname === to ? 'text-[#D4A54A]' : 'text-[#A89070] hover:text-[#D4A54A]'}`} data-testid={testId}>{label}</Link>
  );

  return (
    <header data-testid="main-header" className="bg-[#110A06]/95 backdrop-blur-md border-b border-[#3D2D1D]/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img src={LOGO_URL} alt="KarmaCircle" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#D4A54A]/30" />
            <span className="font-serif text-xl font-semibold text-[#D4A54A]">KarmaCircle</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {isLanding && (
              <>
                <a href="#pricing" className="font-sans text-sm font-medium text-[#A89070] hover:text-[#D4A54A] transition-colors duration-200">Pricing</a>
                <a href="#about" className="font-sans text-sm font-medium text-[#A89070] hover:text-[#D4A54A] transition-colors duration-200">About</a>
              </>
            )}
            {user && (
              <>
                {navLink('/dashboard', 'Dashboard', 'nav-dashboard')}
                {navLink('/events', 'Events', 'nav-events')}
                {navLink('/leaderboard', 'Leaderboard', 'nav-leaderboard')}
                {navLink('/subscription', 'Subscription', 'nav-subscription')}
              </>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user && (
              <>
                {/* Notifications bell */}
                <DropdownMenu open={notifOpen} onOpenChange={(open) => { setNotifOpen(open); if (open && notifCount > 0) markRead(); }}>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="notifications-bell" className="relative p-2 text-[#A89070] hover:text-[#D4A54A] transition-colors">
                      <Bell className="w-5 h-5" strokeWidth={1.5} />
                      {notifCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-[#D4A54A] text-[#110A06] text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">
                          {notifCount}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-[#1A1008] border border-[#3D2D1D] max-h-[400px] overflow-y-auto">
                    <div className="p-3 border-b border-[#3D2D1D]">
                      <p className="font-sans text-xs uppercase tracking-widest font-bold text-[#D4A54A]">Notifications</p>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="font-sans text-sm text-[#A89070]">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.slice(0, 10).map(n => (
                        <div key={n.notif_id} className={`p-3 border-b border-[#3D2D1D]/30 ${!n.read ? 'bg-[#D4A54A]/5' : ''}`}>
                          <p className="font-sans text-sm font-medium text-[#F5E6C8]">{n.title}</p>
                          <p className="font-sans text-xs text-[#A89070] mt-0.5">{n.message}</p>
                          <p className="font-sans text-xs text-[#5C4830] mt-1">{new Date(n.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full px-3 py-1.5 border border-[#3D2D1D] hover:border-[#D4A54A]/50 transition-all duration-200 bg-[#1A1008]">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#D4A54A]/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#D4A54A]" />
                      </div>
                    )}
                    <span className="font-sans text-sm font-medium text-[#F5E6C8] hidden sm:block">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#1A1008] border border-[#3D2D1D]">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                    <UserCircle className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/leaderboard')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                    <Trophy className="w-4 h-4 mr-2" /> Leaderboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/events')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                    <Calendar className="w-4 h-4 mr-2" /> Events
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                    <Settings className="w-4 h-4 mr-2" /> Subscription
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator className="bg-[#3D2D1D]" />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">
                        <Shield className="w-4 h-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#3D2D1D]" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[#C0392B] focus:bg-[#23180E]" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" data-testid="login-btn" className="font-sans text-sm font-medium text-[#A89070] hover:text-[#D4A54A] transition-colors duration-200">
                  Log In
                </Link>
                <Link to="/register" data-testid="get-started-btn" className="bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-6 py-2 font-sans text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20">
                  Get Started
                </Link>
              </div>
            )}

            <button className="md:hidden p-2 text-[#A89070]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-[#3D2D1D] pt-4">
            <nav className="flex flex-col gap-3">
              {isLanding && (
                <>
                  <a href="#pricing" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Pricing</a>
                  <a href="#about" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>About</a>
                </>
              )}
              {user && (
                <>
                  <Link to="/dashboard" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
                  <Link to="/events" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Events</Link>
                  <Link to="/leaderboard" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Leaderboard</Link>
                  <Link to="/subscription" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Subscription</Link>
                  <Link to="/profile" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Profile</Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="font-sans text-sm font-medium text-[#A89070] py-2" onClick={() => setMobileOpen(false)}>Admin</Link>
                  )}
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
